<script lang="ts">
import { computed, onBeforeUnmount, onMounted, toRef } from 'vue';

import { ScrollContainer } from '../../Atoms/ScrollContainer';

import { useActiveTab } from './composables/use-active-tab';
import PhxsgTabButton from './phxsg-tab-button.vue';
import PhxsgTabIndicator from './phxsg-tab-indicator.vue';

import type { PhxsgTabsLayout, PhxsgTabsOrientation, PhxsgTabsPanelData } from './tabs.types';

export interface PhxsgTabsWrapperProps {
  instanceId: string;
  value: string;
  tabs: PhxsgTabsPanelData[];
  tabButtonClasses?: string[] | string;
  tabButtonsIcon?: string;
  orientation?: PhxsgTabsOrientation;
  layout: PhxsgTabsLayout;
  showIndicator?: boolean;
  indicatorThickness?: number;
  rootElement?: HTMLElement | null;
}
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<PhxsgTabsWrapperProps>(), {
  tabButtonClasses: () => [],
  tabButtonsIcon: '',
  orientation: 'horizontal',
  showIndicator: true,
  indicatorThickness: 2,
  rootElement: null,
});

const emit = defineEmits<{
  'tab-content-focus': [event: KeyboardEvent];
  'tab-click': [payload: { id: string; position: number }, event: MouseEvent | KeyboardEvent];
}>();

const {
  activeTabPosition,
  scrollOffset,
  tabsWrapper,
  onScrollOffsetCalc,
  onNavigation,
  onActiveRectUpdate,
  onSetActiveElement,
} = useActiveTab(
  {
    tabs: toRef(props, 'tabs'),
    currentValue: toRef(props, 'value'),
    orientation: toRef(props, 'orientation'),
    rootElement: toRef(props, 'rootElement'),
  },
  { emit },
);

const showStart = computed(() => props.layout === 'start' && scrollOffset.left > 0);
const showEnd = computed(() => props.layout === 'start' && scrollOffset.right > 0);

let scrollContainer: ScrollContainer | null = null;

onMounted(() => {
  if (tabsWrapper.value) {
    scrollContainer = new ScrollContainer(tabsWrapper.value);
  }
});

onBeforeUnmount(() => {
  scrollContainer?.destroy();
  scrollContainer = null;
});

function getResolvedIconPath(tab: PhxsgTabsPanelData) {
  const resolvedIcon = tab.icon || props.tabButtonsIcon;

  return tab.iconPath || (resolvedIcon && tab.iconSpritePath ? `${tab.iconSpritePath}#${resolvedIcon}` : '');
}
</script>

<template>
  <div class="phxsg-tabs__container">
    <div v-if="showStart" class="phxsg-tabs__slot phxsg-tabs__slot--start">
      <slot name="start" />
    </div>

    <div class="phxsg-tabs__scroll">
      <div ref="tabsWrapper" class="phxsg-tabs__wrapper phxsg-scroll-container" @scroll="onScrollOffsetCalc">
        <ul class="phxsg-tabs__list" role="tablist" :aria-orientation="props.orientation" @keydown="onNavigation">
          <PhxsgTabButton
            v-for="tab in props.tabs"
            :key="tab.id"
            :instance-id="props.instanceId"
            :tab="tab"
            :layout="props.layout"
            :class-names="props.tabButtonClasses"
            :icon="tab.icon || props.tabButtonsIcon"
            :icon-path="getResolvedIconPath(tab)"
            :icon-first="tab.icon !== ''"
            :active="props.value === tab.id"
            @tab-click="
              (_, event) =>
                emit(
                  'tab-click',
                  {
                    id: tab.id,
                    position: props.tabs.findIndex(({ id }) => id === tab.id),
                  },
                  event,
                )
            "
            @tab-resize="onActiveRectUpdate"
            @tab-active="onSetActiveElement"
          />
        </ul>

        <PhxsgTabIndicator
          v-if="props.showIndicator"
          :dimensions="activeTabPosition"
          :orientation="props.orientation"
          :thickness="props.indicatorThickness"
        />
      </div>
    </div>

    <div v-if="showEnd" class="phxsg-tabs__slot phxsg-tabs__slot--end">
      <slot name="end" />
    </div>
  </div>
</template>
